import {
    Box,
    FormLabel,
    Heading,
    Input,
    Button,
    Text,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    TabPanels,
    Alert,
    AlertIcon,
    useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Formik, Form } from 'formik';
import { SecretField } from '../components/SecretField';
import * as yup from 'yup';
import { StyledDropzone } from '../components/StyledDropzone';

export type LicenseDistributionInfo =
    | {
          steamAppID?: string;
          steamPartnerKey?: string;
          appStoreSecret?: string;
          playStoreKey?: string;
          twitchClientID?: string;
          twitchClientSecret?: string;
      }
    | undefined;

export type LicenseCreationParams = {
    name: string;
    distInfo: LicenseDistributionInfo;
};

const CreateLicenseSchema = yup.object().shape({
    name: yup.string().required('Game name is required'),
});

interface LicenseFormParams {
    onSubmit: (params: LicenseCreationParams) => any;
    initialName?: string;
    initialDistChannel?: string;
    initialSteamAppID?: string;
    initialSteamPartnerKey?: string;
    initialAppStoreSecret?: string;
    initialPlayStoreKey?: string;
    initialTwitchClientID?: string;
    initialTwitchClientSecret?: string;
}

export const LicenseForm: React.FC<LicenseFormParams> = ({
    onSubmit,
    initialName,
    initialDistChannel,
    initialSteamAppID,
    initialSteamPartnerKey,
    initialAppStoreSecret,
    initialPlayStoreKey,
    initialTwitchClientID,
    initialTwitchClientSecret,
}) => {
    const psCaption1 =
        "Drag 'n' drop Google Play Key JSON File here, or click to select a file";
    const psCaption2 =
        "Drag 'n' drop Google Play Key JSON File here, or click to select a file to update";
    const [psCaption, setPsCaption] = useState('');
    const toast = useToast();

    return (
        <Formik
            initialValues={{
                name: initialName ?? '',
                distChannel: initialDistChannel || 'none',
                steamAppID: initialSteamAppID ?? '',
                steamPartnerKey: initialSteamPartnerKey ?? '',
                appStoreSecret: initialAppStoreSecret ?? '',
                playStoreKey: initialPlayStoreKey ?? '',
                twitchClientID: initialTwitchClientID ?? '',
                twitchClientSecret: initialTwitchClientSecret ?? '',
            }}
            validationSchema={CreateLicenseSchema}
            onSubmit={(values, { setSubmitting }) => {
                setTimeout(() => {
                    const distInfo: LicenseDistributionInfo = {
                        steamAppID: values.steamAppID,
                        steamPartnerKey: values.steamPartnerKey,
                        appStoreSecret: values.appStoreSecret,
                        playStoreKey: values.playStoreKey,
                        twitchClientID: values.twitchClientID,
                        twitchClientSecret: values.twitchClientSecret,
                    };
                    onSubmit({
                        name: values.name,
                        distInfo: Object.fromEntries(
                            Object.entries(distInfo)
                                .map(([_, v]) => [
                                    _,
                                    typeof v === 'string' ? v.trim() : v,
                                ])
                                .filter(([_, v]) => v)
                        ),
                    });
                    setSubmitting(false);
                }, 400);
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
            }) => {
                const onPlayStoreKeySelected = (acceptedFiles: any) => {
                    setPsCaption(acceptedFiles[0].name);
                    const reader = new FileReader();
                    reader.onload = () => {
                        const str = reader.result?.toString();
                        if (str) {
                            if (str.includes('private_key')) {
                                console.log('Key successfully added');
                                setFieldValue('playStoreKey', str);
                                return toast({
                                    title: 'Success',
                                    description: 'Play Store Key added',
                                    status: 'success',
                                    duration: 9000,
                                    isClosable: true,
                                });
                            } else {
                                console.error(
                                    'Not a valid Play Store Key. Please try a different file.'
                                );
                                setFieldValue('playStoreKey', '');
                                setPsCaption('');
                                return toast({
                                    title: 'Invalid Upload',
                                    description:
                                        'File entered is not a valid Play Store Key. Please try again.',
                                    status: 'error',
                                    duration: 9000,
                                    isClosable: true,
                                });
                            }
                        }
                    };
                    reader.readAsText(acceptedFiles[0]);
                };

                return (
                    <Form onSubmit={handleSubmit}>
                        <Box>
                            <FormLabel>
                                <Heading as="h4" size="md">
                                    Game Name
                                </Heading>
                            </FormLabel>
                            <Text color="red">
                                {errors.name && touched.name && errors.name}
                            </Text>
                            <Input
                                placeholder="My First Game"
                                type="text"
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                            />
                        </Box>
                        <Box>
                            <FormLabel>
                                <Heading as="h4" size="md">
                                    Third-Party Integrations
                                </Heading>
                            </FormLabel>
                            <Tabs>
                                <TabList>
                                    <Tab>Steam</Tab>
                                    <Tab>App Store</Tab>
                                    <Tab>Play Store</Tab>
                                    <Tab>Twitch</Tab>
                                </TabList>

                                <TabPanels>
                                    <TabPanel>
                                        <Box>
                                            <FormLabel>Steam App ID</FormLabel>
                                            <Input
                                                type="number"
                                                name="steamAppID"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.steamAppID}
                                            />
                                            <FormLabel>
                                                Steam Publisher Web API Key
                                            </FormLabel>
                                            <SecretField
                                                name="steamPartnerKey"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.steamPartnerKey}
                                            />
                                        </Box>
                                    </TabPanel>
                                    <TabPanel>
                                        <Box>
                                            <FormLabel>
                                                App Store Secret
                                            </FormLabel>
                                            <SecretField
                                                name="appStoreSecret"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.appStoreSecret}
                                            />
                                        </Box>
                                    </TabPanel>
                                    <TabPanel>
                                        <Box>
                                            <FormLabel>
                                                Play Store Key
                                            </FormLabel>
                                            <StyledDropzone
                                                name="playStoreKey"
                                                caption={
                                                    psCaption ||
                                                    (values.playStoreKey
                                                        ? psCaption2
                                                        : psCaption1)
                                                }
                                                onDrop={onPlayStoreKeySelected}
                                            />
                                            {values.playStoreKey && (
                                                <Button
                                                    marginTop={'1em'}
                                                    onClick={() => {
                                                        setFieldValue(
                                                            'playStoreKey',
                                                            ''
                                                        );
                                                        setPsCaption('');
                                                    }}
                                                >
                                                    Clear Play Store Key
                                                </Button>
                                            )}
                                        </Box>
                                    </TabPanel>
                                    <TabPanel>
                                        <Box>
                                            <FormLabel>
                                                Twitch Client ID
                                            </FormLabel>
                                            <Input
                                                name="twitchClientID"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.twitchClientID}
                                            />
                                            <FormLabel>
                                                Twitch Client Secret
                                            </FormLabel>
                                            <SecretField
                                                name="twitchClientSecret"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={
                                                    values.twitchClientSecret
                                                }
                                            />
                                        </Box>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </Box>
                        <Button
                            type="submit"
                            marginTop="0.5em"
                            disabled={isSubmitting}
                        >
                            Submit
                        </Button>
                    </Form>
                );
            }}
        </Formik>
    );
};
